import * as api from '@/features/backlog/api';

/** Web Push needs a service worker, the Push API, and the Notification API — all three, over HTTPS. */
export const pushSupported = () =>
  typeof navigator !== 'undefined' &&
  'serviceWorker' in navigator &&
  typeof window !== 'undefined' &&
  'PushManager' in window &&
  'Notification' in window;

const SW_URL = '/sw.js';

async function registerSW(): Promise<ServiceWorkerRegistration> {
  // Always register (idempotent): guarantees OUR /sw.js owns the scope and picks up any deployed
  // update, instead of silently reusing a stale or foreign registration that wouldn't handle push.
  const reg = await navigator.serviceWorker.register(SW_URL);
  try {
    await reg.update();
  } catch {
    /* update is best-effort */
  }
  return reg;
}

/** Decode a base64url VAPID public key into the Uint8Array `applicationServerKey` expects. */
function vapidKeyToBytes(base64url: string): Uint8Array {
  const padding = '='.repeat((4 - (base64url.length % 4)) % 4);
  const b64 = (base64url + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(b64);
  const bytes = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) bytes[i] = raw.charCodeAt(i);
  return bytes;
}

/** For THIS browser: 'unsupported' (browser can't), 'unavailable' (server has no VAPID keys),
 *  'denied' (permission blocked), 'off' (supported but not subscribed), 'on' (subscribed). */
export type PushState = 'unsupported' | 'unavailable' | 'denied' | 'off' | 'on';

/** Resolve the current state for this browser (also tells us if the server supports push at all). */
export async function getPushState(): Promise<PushState> {
  if (!pushSupported()) return 'unsupported';
  let serverKey = '';
  try {
    serverKey = (await api.pushPublicKey()).public_key;
  } catch {
    serverKey = '';
  }
  if (!serverKey) return 'unavailable';
  if (Notification.permission === 'denied') return 'denied';
  try {
    // Ensure our SW is registered + current, so a member who turned push on keeps a working worker
    // (a deploy that changed sw.js, or a browser that dropped a stale one, self-heals on this load).
    const reg = await registerSW();
    const sub = await reg.pushManager.getSubscription();
    if (!sub) return 'off';
    // Re-sync so the server always holds THIS browser's subscription for the CURRENT member — repairs a
    // failed initial save, a server-side prune (410), or a browser now used by a different member.
    const j = sub.toJSON() as { endpoint?: string; keys?: { p256dh?: string; auth?: string } };
    if (j.endpoint && j.keys?.p256dh && j.keys?.auth) {
      void api.savePushSubscription({ endpoint: j.endpoint, keys: { p256dh: j.keys.p256dh, auth: j.keys.auth } }).catch(() => undefined);
    }
    return 'on';
  } catch {
    return 'off';
  }
}

/** Enable push on this browser: request permission → subscribe → register with the server. */
export async function enablePush(): Promise<PushState> {
  if (!pushSupported()) return 'unsupported';
  const key = (await api.pushPublicKey().catch(() => ({ public_key: '' }))).public_key;
  if (!key) return 'unavailable';
  const perm = await Notification.requestPermission();
  if (perm !== 'granted') return perm === 'denied' ? 'denied' : 'off';
  const reg = await registerSW();
  await navigator.serviceWorker.ready;
  // Cast: applicationServerKey wants BufferSource; a Uint8Array is one, but TS's generic
  // Uint8Array<ArrayBufferLike> doesn't narrow to it automatically.
  const opts = { userVisibleOnly: true, applicationServerKey: vapidKeyToBytes(key) as BufferSource };
  let sub: PushSubscription;
  try {
    sub = await reg.pushManager.subscribe(opts);
  } catch {
    // A leftover subscription made with a DIFFERENT VAPID key rejects with InvalidStateError — drop it
    // and subscribe fresh so a key change (or a half-set-up state) can't wedge the toggle.
    const existing = await reg.pushManager.getSubscription();
    if (existing) await existing.unsubscribe();
    sub = await reg.pushManager.subscribe(opts);
  }
  const j = sub.toJSON() as { endpoint?: string; keys?: { p256dh?: string; auth?: string } };
  if (!j.endpoint || !j.keys?.p256dh || !j.keys?.auth) return 'off';
  await api.savePushSubscription({ endpoint: j.endpoint, keys: { p256dh: j.keys.p256dh, auth: j.keys.auth } });
  return 'on';
}

/** Disable push on this browser: unsubscribe locally + remove the subscription server-side. */
export async function disablePush(): Promise<PushState> {
  if (!pushSupported()) return 'unsupported';
  try {
    const reg = await navigator.serviceWorker.getRegistration();
    const sub = reg ? await reg.pushManager.getSubscription() : null;
    if (sub) {
      const endpoint = sub.endpoint;
      await sub.unsubscribe();
      await api.removePushSubscription(endpoint).catch(() => undefined);
    }
  } catch {
    /* ignore */
  }
  return 'off';
}
